// src/components/AdminDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import CreatableSelect from 'react-select/creatable';
import darkSelectStyles from '../utils/darkSelectStyles';
import useCylinderSummary from '../hooks/useCylinderSummary';
import { getUnifiedMovements } from '../utils/movementsHelper';
import { formatDateTime } from '../utils/formatDate';
import CylinderSummaryDashboard from './CylinderSummaryDashboard';
import MovementsTable from './movementsTable';
import PlantHealthPanel from './PlantHealthPanel';
import CompliancePanel from './CompliancePanel';
import OperationsInsightsPanel from './OperationsInsightsPanel';
import PickupsDeliveriesPanel from './PickupsDeliveriesPanel';
import SealsPanel from './SealsPanel';
import CgcTanksPanel from './CgcTanksPanel';
import VentasSyncPanel from './VentasSyncPanel';
import { APPROVAL_APPLIED } from './ApprovalsPanel';
import StaffActivityPanel from './StaffActivityPanel';
import Card from 'react-bootstrap/Card';
import ProductionHoursBarChart from './ProductionHoursBarChart';
import FillingGoalPieChart from './FillingGoalPieChart';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useFilling } from '../context/FillingContext';
import { GASES, GAS_LABEL, gasLabel, isCgcClient, CGC_CLIENT } from '../utils/gases';



import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

const CURRENT_YEAR = new Date().getFullYear();
// Plant has operated since 2025 — no data (and no reason to plan a goal) before that.
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 1 - 2025 + 1 }, (_, i) => 2025 + i);

const roleIcon = (role) => {
  if (role === 'admin') return 'bi-shield-lock-fill';
  if (role === 'filler') return 'bi-droplet-fill';
  if (role === 'sales') return 'bi-cart-fill';
  return 'bi-person-fill';
};

const roleBadgeClass = (role) => {
  if (role === 'admin') return 'bg-success';
  if (role === 'filler') return 'bg-warning text-dark';
  if (role === 'sales') return 'bg-primary';
  return 'bg-secondary';
};


const AdminDashboard = ({
  users,
  setUsers,
  addLogEntry,
  userName,
  cylinderContributions,
  setCylinderContributions,
}) => {
  const [newUser, setNewUser] = useState({ username: '', password: '', type: 'filler' });
  const [userSearch, setUserSearch] = useState('');

  const [clientsList, setClientsList] = useState([]);
  const [contributionClient, setContributionClient] = useState('');
  // The gas of the empties, and for CGC's own empties who brought them back (backend utils/cgcTanks.js).
  const [contributionGas, setContributionGas] = useState('');
  const [contributionBroughtBy, setContributionBroughtBy] = useState('');
  const [additionalCylinders, setAdditionalCylinders] = useState(0);
  const [cylindersToday, setCylindersToday] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [avgTime, setAvgTime] = useState(0);
  const [avgRange, setAvgRange] = useState('24h');
  const [cylinderCounts, setCylinderCounts] = useState({ today: 0, remaining: 0 });

const [filledCountWeek, setFilledCountWeek] = useState(0);
const [filledCountMonth, setFilledCountMonth] = useState(0);
// The month card's full answer ({ count, live, historical, ... }) plus WHICH month it was for, so a slow response for the month
// just left can never be shown against the month now selected.
const [filledMonthInfo, setFilledMonthInfo] = useState(null);
const [filledCountYTD, setFilledCountYTD] = useState(0);
// YTD and all-time also carry `historical` (tanks from before Manifold Manager recorded fillings itself, from the previous
// system) and `live` (recorded here), so the cards can say where the number comes from.
const [filledYTDInfo, setFilledYTDInfo] = useState(null);
const [filledAllInfo, setFilledAllInfo] = useState(null);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [userToDelete, setUserToDelete] = useState(null);
const [roleSavingUsername, setRoleSavingUsername] = useState('');
const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
const [resetPasswordTarget, setResetPasswordTarget] = useState('');
const [resetPasswordValue, setResetPasswordValue] = useState('');
const [resetPasswordSaving, setResetPasswordSaving] = useState(false);
const [resetPasswordError, setResetPasswordError] = useState('');

 const [productionData, setProductionData] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1); // 1-indexed
  const [year, setYear] = useState(new Date().getFullYear());

  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState('');

  // Shown until the first movements fetch resolves, instead of rendering
  // the whole dashboard with every card at its zeroed default for a beat.
  const [initialLoading, setInitialLoading] = useState(true);

  // Quick-add ("+") on the Movimientos de Cilindros card — lets an admin
  // create a contribution or a despacho without leaving that card. The
  // contribution tab reuses the exact same state/handler/confirm-modal as
  // the "Cilindros En Planta" card's own add form (contributionClient,
  // additionalCylinderType, additionalCylinders, handleAddCylinders) so
  // there's only one contribution-creation code path. Despacho has no
  // existing admin-side creation flow, so it gets its own.
  const [showAddMovementModal, setShowAddMovementModal] = useState(false);
  const [addMovementType, setAddMovementType] = useState('contribution');

  const [dispatchClientGroups, setDispatchClientGroups] = useState([]);
  const [dispatchModalClient, setDispatchModalClient] = useState(''); // a stock group's key: client|gas
  const [dispatchModalAmount, setDispatchModalAmount] = useState(0);
  const [dispatchModalNote, setDispatchModalNote] = useState('');
  const [dispatchModalDestination, setDispatchModalDestination] = useState('');
  const [dispatchModalMethod, setDispatchModalMethod] = useState('entrega'); // entrega (confirmed later) / retiro (delivered now)
  const [addDispatchError, setAddDispatchError] = useState('');
  const [addDispatchSaving, setAddDispatchSaving] = useState(false);
  const [showAddDispatchConfirmModal, setShowAddDispatchConfirmModal] = useState(false);

const [monthlyFillData, setMonthlyFillData] = useState({});
const [movements, setMovements] = useState([]);

// Edit/delete for "Recibido" rows in the Movimientos de Cilindros table —
// these are CylinderEntry contributions, same PATCH/DELETE /api/cylinders/:id
// endpoints salesDashboard.js's own contribution edit/delete already uses.
const [showMovementEditModal, setShowMovementEditModal] = useState(false);
const [editingMovement, setEditingMovement] = useState(null);
const [movementEditForm, setMovementEditForm] = useState({ quantity: '', type: 'CC', date: '' });
const [movementEditSaving, setMovementEditSaving] = useState(false);
const [movementEditError, setMovementEditError] = useState('');

const [showMovementDeleteModal, setShowMovementDeleteModal] = useState(false);
const [movementDeleteTarget, setMovementDeleteTarget] = useState(null);
const [movementDeleteSaving, setMovementDeleteSaving] = useState(false);
const [movementDeleteError, setMovementDeleteError] = useState('');

// Edit/delete for "Despachado" rows — same PATCH/DELETE /api/dispatches/:id
// endpoints salesDashboard.js's own dispatch edit/delete already uses.
const [showDispatchEditModal, setShowDispatchEditModal] = useState(false);
const [editingDispatch, setEditingDispatch] = useState(null);
const [dispatchEditForm, setDispatchEditForm] = useState({
  noteNumber: '',
  dispatchedBy: '',
  destinationClient: '',
  contributions: [],
});
const [dispatchEditSaving, setDispatchEditSaving] = useState(false);
const [dispatchEditError, setDispatchEditError] = useState('');

const [showDispatchDeleteModal, setShowDispatchDeleteModal] = useState(false);
const [dispatchDeleteTarget, setDispatchDeleteTarget] = useState(null);
const [dispatchDeleteSaving, setDispatchDeleteSaving] = useState(false);
const [dispatchDeleteError, setDispatchDeleteError] = useState('');

// Devolución — POST /api/dispatches/:id/return, same endpoint and semantics
// as salesDashboard.js's own devolución modal.
const [showDispatchReturnModal, setShowDispatchReturnModal] = useState(false);
const [returningDispatch, setReturningDispatch] = useState(null);
const [dispatchReturnLines, setDispatchReturnLines] = useState([]);
const [dispatchReturnReason, setDispatchReturnReason] = useState('');
const [dispatchReturnSaving, setDispatchReturnSaving] = useState(false);
const [dispatchReturnError, setDispatchReturnError] = useState('');

// Fillings modal — shows the underlying Filling records behind the "Tanques
// Llenados por Día" / "Rendimiento" / "Tendencia Tiempo de Llenado" charts,
// which all derive from the same month-filtered `entries`, with edit/delete
// right from that table instead of needing a separate screen.
const [showFillingsModal, setShowFillingsModal] = useState(false);
const [fillingsModalDay, setFillingsModalDay] = useState(null);
const [fillingsPage, setFillingsPage] = useState(1);
const FILLINGS_PER_PAGE = 10;

const [showFillingEditModal, setShowFillingEditModal] = useState(false);
const [editingFilling, setEditingFilling] = useState(null);
const [fillingEditForm, setFillingEditForm] = useState({ clients: '', ccCount: '', pcCount: '', startTime: '', endTime: '' });
const [fillingEditSaving, setFillingEditSaving] = useState(false);
const [fillingEditError, setFillingEditError] = useState('');

const [showFillingDeleteModal, setShowFillingDeleteModal] = useState(false);
const [fillingDeleteTarget, setFillingDeleteTarget] = useState(null);
const [fillingDeleteSaving, setFillingDeleteSaving] = useState(false);
const [fillingDeleteError, setFillingDeleteError] = useState('');
const [hoursData, setHoursData] = useState({}); // Operación hours (stream defaults to 'operacion' server-side)
const [productionHours, setProductionHours] = useState(0);
const [totalCylinders, setTotalCylinders] = useState(0);
const [gasHoursData, setGasHoursData] = useState({ o2: [], n2: [] });

const [goal, setGoal] = useState(0);
const [goalInput, setGoalInput] = useState('');
const [goalExists, setGoalExists] = useState(true);

// Goals can be set/edited for the current month or any future one, but past
// months are view-only — history shouldn't be rewritten after the fact.
const isPastGoalMonth = (() => {
  const today = new Date();
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const selectedMonthStart = new Date(year, month - 1, 1);
  return selectedMonthStart < currentMonthStart;
})();
const [activityLog, setActivityLog] = useState([]);
const [activitySearch, setActivitySearch] = useState('');
const [activityUserFilter, setActivityUserFilter] = useState('');
const [activityPage, setActivityPage] = useState(1);
const ACTIVITY_PER_PAGE = 8;


const [adjustedReceived, setAdjustedReceived] = useState(0);
const [adjustedRemaining, setAdjustedRemaining] = useState(0);
const [showFilledConfirmModal, setShowFilledConfirmModal] = useState(false);
const [savingReceivedUpdate, setSavingReceivedUpdate] = useState(false);
const [savingFilledUpdate, setSavingFilledUpdate] = useState(false);
const [showAddCylindersConfirmModal, setShowAddCylindersConfirmModal] = useState(false);
const [savingAddCylinders, setSavingAddCylinders] = useState(false);
const [additionalCylinderType, setAdditionalCylinderType] = useState('CC');


const { fillings, setFillings } = useFilling();
const [entries, setEntries] = useState(fillings);

// Tanques llenados este mes for the goal chart — summed client-side from the
// same `entries` "Ritmo hacia la Meta" already uses, instead of a separate
// backend call. This deliberately INCLUDES admin fill-adjustments (ADJ-
// entries): unlike /api/fillings/count-summary (used by the "Llenado Este
// Mes/Semana/YTD" stat cards), which excludes them as "not real production",
// an adjustment here represents tanks that really were filled but never
// logged individually — leaving them out just made the goal look further
// behind than it actually is, and out of step with "Ritmo hacia la Meta"
// (which already summed every entry, adjustments included).
//
// Months before Manifold recorded fillings (Jan–May 2026, the start of June...) also carry the tanks from the previous system
// (`monthHistorical`, from count-summary), so the selected month's total isn't 0 there. It only counts once the answer for THIS month
// has arrived, and Manifold's own entries all start after that history ends, so nothing is counted twice.
const monthHistorical = filledMonthInfo && filledMonthInfo.forMonth === month && filledMonthInfo.forYear === year
  ? (filledMonthInfo.historical || 0)
  : 0;
const filledGoalCylinders = useMemo(() => {
  return monthHistorical + (Array.isArray(entries) ? entries : []).reduce((sum, entry) => {
    const d = new Date(entry.date);
    if (d.getFullYear() === year && d.getMonth() === month - 1) {
      return sum + (entry.totalCylinders || 0);
    }
    return sum;
  }, 0);
}, [entries, month, year, monthHistorical]);


useEffect(() => {
  if (cylindersToday > 0) {
    setAdjustedReceived(cylindersToday);
  }
}, [cylindersToday]);

useEffect(() => {
  setEntries(fillings);
}, [fillings]);




  const { received, remaining, filled, dispatched, averiados, refreshSummary } = useCylinderSummary();
  // Every "Ajustar" says why (the server refuses one without a reason).
  const [adjustReason, setAdjustReason] = useState('');

  // An admin approved a sales request from the header's Autorizaciones: the numbers here changed.
  useEffect(() => {
    const onApplied = () => { refreshSummary(); fetchMovements(); fetchActivityLog(); };
    window.addEventListener(APPROVAL_APPLIED, onApplied);
    return () => window.removeEventListener(APPROVAL_APPLIED, onApplied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdjustedRemaining(remaining);
  }, [remaining]);

  const barChartData = {
  labels: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
  datasets: [
    {
      label: 'Horas de Producción',
      data: Array.from({ length: 31 }, (_, i) => {
        const matchingDay = productionData.find(day => new Date(day.date).getDate() === i + 1);
        return matchingDay ? matchingDay.hours : 0;
      }),
      backgroundColor: 'rgba(18, 184, 178, 0.6)',
    },
  ],
};

const barChartOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
    title: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Horas' },
    },
    x: {
      title: { display: true, text: 'Día del Mes' },
    },
  },
};

const goalPercentage = goal > 0 ? (filledGoalCylinders / goal) * 100 : 0;
const goalPercentageColor = goalPercentage >= 100 ? 'var(--mm-green)' : goalPercentage >= 70 ? 'var(--mm-yellow)' : 'var(--mm-red)';

const goalPieData = {
  labels: ['Llenado Realizado', 'Meta Restante'],
  datasets: [
    {
      data: [filledGoalCylinders, Math.max(goal - filledGoalCylinders, 0)],
      backgroundColor: ['#12B8B2', 'rgba(234,242,245,0.12)'],
      hoverOffset: 4,
    },
  ],
};

const goalPieOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'bottom' },
  },
};



const chartOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Tanques' }
    },
    x: {
      title: { display: true, text: 'Día del Mes' }
    }
  }
};

const fetchActivityLog = async () => {
  try {
    const res = await fetch('/api/activity-log');

    const data = await res.json();
    setActivityLog(data);
  } catch (err) {
    console.error('❌ Error fetching activity log:', err);
  }
};

useEffect(() => {
  fetchActivityLog();
}, []);

// Registro de Actividades: search + user filter + pagination
const activityUsers = useMemo(() => {
  const set = new Set((activityLog || []).map((l) => l.user).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}, [activityLog]);

const filteredActivityLog = useMemo(() => {
  const q = activitySearch.trim().toLowerCase();
  return (activityLog || []).filter((log) => {
    const d = new Date(log.timestamp);
    const matchesPeriod = d.getFullYear() === year && d.getMonth() === month - 1;
    const matchesUser = !activityUserFilter || log.user === activityUserFilter;
    const matchesSearch = !q || (log.message || '').toLowerCase().includes(q);
    return matchesPeriod && matchesUser && matchesSearch;
  });
}, [activityLog, activitySearch, activityUserFilter, month, year]);

const activityTotalPages = Math.max(1, Math.ceil(filteredActivityLog.length / ACTIVITY_PER_PAGE));
const paginatedActivityLog = filteredActivityLog.slice(
  (activityPage - 1) * ACTIVITY_PER_PAGE,
  activityPage * ACTIVITY_PER_PAGE
);

useEffect(() => {
  setActivityPage(1);
}, [activitySearch, activityUserFilter, month, year]);

useEffect(() => {
  if (activityPage > activityTotalPages) setActivityPage(activityTotalPages);
}, [activityPage, activityTotalPages]);


const fetchMovements = async () => {
  try {
    const res = await fetch('/api/cylinders/movements');
    const data = await res.json();
    setMovements(data);
  } catch (err) {
    console.error('❌ Error fetching movements:', err);
  }
};

const formatDateInput = (date) => {
  const d = date ? new Date(date) : new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const openMovementEditModal = (entry) => {
  setEditingMovement(entry);
  setMovementEditForm({
    quantity: entry.quantity,
    type: entry.cylinderType || 'CC',
    date: formatDateInput(entry.date),
  });
  setMovementEditError('');
  setShowMovementEditModal(true);
};

const saveMovementEdit = async () => {
  if (!editingMovement) return;

  const quantity = parseInt(movementEditForm.quantity, 10);
  if (!quantity || quantity <= 0) {
    setMovementEditError('Ingrese una cantidad válida.');
    return;
  }

  setMovementEditSaving(true);
  setMovementEditError('');

  try {
    const res = await fetch(`/api/cylinders/${editingMovement._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quantity,
        type: movementEditForm.type,
        date: movementEditForm.date,
        userName,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al editar la contribución.');

    await fetchMovements();
    refreshSummary();

    setShowMovementEditModal(false);
    setEditingMovement(null);
  } catch (err) {
    setMovementEditError(err.message);
  } finally {
    setMovementEditSaving(false);
  }
};

const requestMovementDelete = (entry) => {
  setMovementDeleteTarget(entry);
  setMovementDeleteError('');
  setShowMovementDeleteModal(true);
};

const confirmMovementDelete = async () => {
  if (!movementDeleteTarget) return;

  setMovementDeleteSaving(true);
  setMovementDeleteError('');

  try {
    const res = await fetch(`/api/cylinders/${movementDeleteTarget._id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al eliminar la contribución.');

    await fetchMovements();
    refreshSummary();

    setShowMovementDeleteModal(false);
    setMovementDeleteTarget(null);
  } catch (err) {
    setMovementDeleteError(err.message);
  } finally {
    setMovementDeleteSaving(false);
  }
};

const openDispatchEditModal = (dispatch) => {
  setEditingDispatch(dispatch);
  setDispatchEditForm({
    noteNumber: dispatch.noteNumber === '—' ? '' : dispatch.noteNumber,
    dispatchedBy: dispatch.dispatchedBy || '',
    destinationClient: dispatch.contributions?.[0]?.destinationClient || '',
    contributions: (dispatch.contributions || []).map((c) => ({
      entryId: c.entryId,
      client: c.client,
      amount: c.amount,
    })),
  });
  setDispatchEditError('');
  setShowDispatchEditModal(true);
};

const updateDispatchEditAmount = (index, value) => {
  setDispatchEditForm((f) => {
    const contributions = [...f.contributions];
    contributions[index] = { ...contributions[index], amount: value };
    return { ...f, contributions };
  });
};

const saveDispatchEdit = async () => {
  if (!editingDispatch) return;

  if (!dispatchEditForm.noteNumber.trim()) {
    setDispatchEditError('Ingrese un número de documento válido.');
    return;
  }

  const contributions = dispatchEditForm.contributions.map((c) => ({
    entryId: c.entryId,
    client: c.client,
    amount: parseInt(c.amount, 10),
    destinationClient: dispatchEditForm.destinationClient || undefined,
  }));

  if (contributions.some((c) => !c.amount || c.amount <= 0)) {
    setDispatchEditError('Todas las cantidades deben ser mayores a cero.');
    return;
  }

  setDispatchEditSaving(true);
  setDispatchEditError('');

  try {
    const res = await fetch(`/api/dispatches/${editingDispatch._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        noteNumber: dispatchEditForm.noteNumber.trim(),
        dispatchedBy: dispatchEditForm.dispatchedBy || userName,
        contributions,
        userName,
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(
        data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al editar el despacho.')
      );
    }

    await fetchMovements();
    refreshSummary();

    setShowDispatchEditModal(false);
    setEditingDispatch(null);
  } catch (err) {
    setDispatchEditError(err.message);
  } finally {
    setDispatchEditSaving(false);
  }
};

const requestDispatchDelete = (dispatch) => {
  setDispatchDeleteTarget(dispatch);
  setDispatchDeleteError('');
  setShowDispatchDeleteModal(true);
};

const confirmDispatchDelete = async () => {
  if (!dispatchDeleteTarget) return;

  setDispatchDeleteSaving(true);
  setDispatchDeleteError('');

  try {
    const res = await fetch(`/api/dispatches/${dispatchDeleteTarget._id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(
        data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al eliminar el despacho.')
      );
    }

    await fetchMovements();
    refreshSummary();

    setShowDispatchDeleteModal(false);
    setDispatchDeleteTarget(null);
  } catch (err) {
    setDispatchDeleteError(err.message);
  } finally {
    setDispatchDeleteSaving(false);
  }
};

const openDispatchReturnModal = (dispatch) => {
  const alreadyReturnedByEntry = {};
  (dispatch.returns || []).forEach((r) => {
    alreadyReturnedByEntry[r.entryId] = (alreadyReturnedByEntry[r.entryId] || 0) + (r.quantity || 0);
  });

  const lines = (dispatch.contributions || []).map((c) => {
    const alreadyReturned = alreadyReturnedByEntry[c.entryId] || 0;
    return {
      entryId: c.entryId,
      client: c.client,
      originalAmount: c.amount,
      alreadyReturned,
      remaining: c.amount - alreadyReturned,
      returnQty: '',
    };
  });

  setReturningDispatch(dispatch);
  setDispatchReturnLines(lines);
  setDispatchReturnReason('');
  setDispatchReturnError('');
  setShowDispatchReturnModal(true);
};

const updateDispatchReturnQty = (index, value) => {
  setDispatchReturnLines((lines) => {
    const next = [...lines];
    next[index] = { ...next[index], returnQty: value };
    return next;
  });
};

const saveDispatchReturn = async () => {
  if (!returningDispatch) return;

  const returns = dispatchReturnLines
    .map((l) => ({ entryId: l.entryId, quantity: parseInt(l.returnQty, 10), remaining: l.remaining }))
    .filter((l) => Number.isFinite(l.quantity) && l.quantity > 0);

  if (returns.length === 0) {
    setDispatchReturnError('Ingrese al menos una cantidad a devolver.');
    return;
  }

  const overLimit = returns.find((l) => l.quantity > l.remaining);
  if (overLimit) {
    setDispatchReturnError('Una de las cantidades excede lo pendiente para esa línea.');
    return;
  }

  setDispatchReturnSaving(true);
  setDispatchReturnError('');

  try {
    const res = await fetch(`/api/dispatches/${returningDispatch._id}/return`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        returns: returns.map(({ entryId, quantity }) => ({ entryId, quantity })),
        reason: dispatchReturnReason.trim() || undefined,
      }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(
        data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al registrar la devolución.')
      );
    }

    await fetchMovements();
    refreshSummary();

    setShowDispatchReturnModal(false);
    setReturningDispatch(null);
  } catch (err) {
    setDispatchReturnError(err.message);
  } finally {
    setDispatchReturnSaving(false);
  }
};

const handleGenerateReport = async () => {
  setGeneratingReport(true);
  setReportError('');
  try {
    const res = await fetch(`/api/reports/generate?month=${month}&year=${year}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Error al generar el reporte.');
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-operativo-${year}-${String(month).padStart(2, '0')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    setReportError(err.message);
  } finally {
    setGeneratingReport(false);
  }
};

useEffect(() => {
  fetchMovements().finally(() => setInitialLoading(false));
}, []);


// Production hours for the selected month/year — single source of truth for
// both the bar chart and the "horas de producción / tanques llenados" readout below it.
useEffect(() => {
  const fetchProductionHours = async () => {
    try {
      const res = await fetch(`/api/production-events/hours?month=${month}&year=${year}`);
      const data = await res.json();
      setHoursData({
        calculatedHours: data.calculatedHours || [],
        snapshotHours: data.snapshotHours || [],
      });
    } catch (err) {
      console.error('❌ Error fetching production hours:', err);
    }
  };

  fetchProductionHours();
}, [month, year]);

// O2/N2 production hours for the same month/year — separate streams,
// separate figures (not combined into the Operación number above).
useEffect(() => {
  const fetchGasHours = async () => {
    try {
      const [o2Res, n2Res] = await Promise.all([
        fetch(`/api/production-events/hours?month=${month}&year=${year}&stream=o2`),
        fetch(`/api/production-events/hours?month=${month}&year=${year}&stream=n2`),
      ]);
      const [o2Data, n2Data] = await Promise.all([o2Res.json(), n2Res.json()]);
      setGasHoursData({ o2: o2Data.calculatedHours || [], n2: n2Data.calculatedHours || [] });
    } catch (err) {
      console.error('❌ Error fetching O2/N2 production hours:', err);
    }
  };

  fetchGasHours();
}, [month, year]);


  const fetchDashboardData = async () => {
    try {
      const res = await fetch('/api/fillings/admin');
      const data = await res.json();
      setEntries(data.entries || []);
      setUsers(data.users || []);
    } catch (err) {
      console.error('❌ Failed to fetch admin data:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch('/api/clients');
      const data = await res.json();
      if (Array.isArray(data)) {
        setClientsList(data.map(name => ({ label: name, value: name })));
      }
    } catch (err) {
      console.error('❌ Failed to fetch clients:', err);
    }
  };

const fetchContributions = async () => {
  try {
    const res = await fetch('/api/cylinders/summary');
    const data = await res.json();

    if (Array.isArray(data.contributions)) {
      const formatted = data.contributions.map(entry => ({
        clientName: entry.client,
        cylindersAdded: entry.amount,
        timestamp: formatDateTime(entry.date, '-'),
      }));
      setCylinderContributions(formatted);
    }
  } catch (err) {
    console.error('❌ Error fetching contributions summary:', err);
  }
};


  const fetchCylinderCounts = async () => {
    try {
      const res = await fetch('/api/cylinders/summary');
      const data = await res.json();
      if (data && typeof data.received === 'number') {
        setCylinderCounts({
          today: data.received,
          remaining: data.remaining,
        });
        setCylindersToday(data.received);
      }
    } catch (err) {
      console.error('❌ Failed to fetch cylinder counts:', err);
    }
  };

  const fetchAverageTime = async () => {
    try {
      const res = await fetch(`/api/fillings/average?range=${avgRange}`);
      const data = await res.json();
      setAvgTime(data.averageFillingTime || 0);
    } catch (err) {
      console.error('❌ Error fetching average time:', err);
    }
  };

  const fetchFilledCountMonth = async () => {
  try {
    // count-summary's explicit month/year branch expects a 0-indexed month
    // (it feeds straight into monthRangeDR without adjustment) — unlike
    // production-events/hours below, which expects 1-indexed.
    // includeAdjustments=1: the month card counts the admin adjustments too (like YTD and the goal chart), so a month whose
    // tanks were entered as adjustments isn't shown as almost empty.
    const res = await fetch(`/api/fillings/count-summary?month=${month - 1}&year=${year}&includeAdjustments=1`);
    const data = await res.json();
    setFilledCountMonth(data.count || 0);
    setFilledMonthInfo({ ...data, forMonth: month, forYear: year });
  } catch (err) {
    console.error('❌ Error fetching monthly count:', err);
  }
};

const fetchFilledCountWeek = async () => {
  try {
    const res = await fetch('/api/fillings/count-summary?range=7d');
    const data = await res.json();
    setFilledCountWeek(data.count || 0);
  } catch (err) {
    console.error('❌ Error fetching weekly count:', err);
  }
};

const fetchFilledCountYTD = async () => {
  try {
    const res = await fetch('/api/fillings/count-summary?range=ytd');
    const data = await res.json();
    setFilledCountYTD(data.count || 0);
    setFilledYTDInfo(data);
  } catch (err) {
    console.error('❌ Error fetching YTD count:', err);
  }
};

const fetchFilledCountAll = async () => {
  try {
    const res = await fetch('/api/fillings/count-summary?range=all');
    setFilledAllInfo(await res.json());
  } catch (err) {
    console.error('❌ Error fetching all-time count:', err);
  }
};

// "2026-06-22" → "22 jun 2026", for the captions that say where a counter's history comes from.
const formatDayEs = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });
const formatMonthEs = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-DO', { month: 'short', year: 'numeric' });
const thousands = (n) => Number(n || 0).toLocaleString('es-DO');

useEffect(() => {
  const fetchMonthlyFillings = async () => {
    try {
      const res = await fetch('/api/fillings/month-summary');
      const data = await res.json();
      setMonthlyFillData(data);
    } catch (err) {
      console.error('Error loading monthly fillings chart:', err);
    }
  };

  fetchMonthlyFillings();
}, []);


  useEffect(() => {
    fetchDashboardData();
    fetchClients();
    fetchContributions();
    fetchCylinderCounts();
    fetchFilledCountWeek();
    fetchFilledCountYTD();
    fetchFilledCountAll();
  }, []);

  useEffect(() => {
    fetchFilledCountMonth();
  }, [month, year]);

  useEffect(() => {
    fetchAverageTime();
  }, [avgRange]);


// Build chartData based on fetched data (keyed by the same month/year the fetch above uses)
const labels = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

// Chart.js ticks.color callback factory — flags Saturdays/Sundays on any
// day-of-month x-axis in the theme green, everything else in the same muted
// gray ChartJS.defaults.color already uses. Canvas can't resolve
// var(--mm-green), so the literal hex (matching that same default's own
// literal in App.js) is used instead. `zeroIndexedMonth` lets callers pass
// either month convention this file already mixes (the "Producción y
// Metas" selector is 1-indexed, the "Meta de llenados" selector is 0-indexed).
const weekendTickColor = (dayLabels, y, zeroIndexedMonth) => (ctx) => {
  const day = parseInt(dayLabels[ctx.index], 10);
  const dow = new Date(y, zeroIndexedMonth, day).getDay();
  return (dow === 0 || dow === 6) ? '#4ADE80' : '#9DB3C2';
};

// One dataset-builder reused for all three streams — same `labels` (1-31)
// and day-key lookup, just pointed at a different hours array.
const buildHoursDataset = (label, color, hoursArr) => ({
  label,
  backgroundColor: color,
  borderColor: color,
  borderWidth: 1,
  data: labels.map((day) => {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const match = hoursArr.find(h => h.date === key);
    return match ? match.hours : 0;
  }),
});

// Horas de Operación / O2 / N2 as three datasets on the same chart, not
// three separate charts — O2/N2 hours are a subset of Operación hours
// (gas can only run while Operación is running), so grouped bars read
// correctly here; stacking them would double-count.
const chartData = {
  labels,
  datasets: [
    buildHoursDataset('Horas de Operación', 'rgba(18, 184, 178, 0.6)', hoursData.calculatedHours || []),
    buildHoursDataset('Horas de Producción O2', 'rgba(80, 140, 255, 0.6)', gasHoursData.o2),
    buildHoursDataset('Horas de Producción N2', 'rgba(255, 170, 40, 0.6)', gasHoursData.n2),
  ],
};



useEffect(() => {
  const fetchGoal = async () => {
    try {
      // Goal collection was already storing 0-indexed months before this
      // selector was unified with the (1-indexed) production charts one —
      // converting here avoids a data migration.
      const res = await fetch(`/api/goals?month=${month - 1}&year=${year}`);
      const data = await res.json();
      if (data.goal !== undefined) {
        setGoal(data.goal);
        setGoalInput(data.goal);
        setGoalExists(true);
      } else {
        setGoal(0);
        setGoalInput('');
        setGoalExists(false);
      }
    } catch (err) {
      console.error('❌ Error al obtener la meta:', err);
      setGoalExists(false);
    }
  };
  fetchGoal();
}, [month, year]);


useEffect(() => {
  const fetchStats = async () => {
    try {
      const res1 = await fetch(`/api/production/hours?month=${month}&year=${year}`);
      const data1 = await res1.json();

      const totalProdHours =
        [...data1.calculatedHours, ...data1.snapshotHours].reduce(
          (sum, item) => sum + (item.hours || 0),
          0
        );
      setProductionHours(totalProdHours);

      const res2 = await fetch('/api/fillings');
      const data2 = await res2.json();

      const filteredFillings = data2.filter(entry => {
        const date = new Date(entry.date);
        return (
          date.getMonth() + 1 === month &&
          date.getFullYear() === year
        );
      });

      const totalFilled = filteredFillings.reduce((sum, entry) => {
        if ('totalCylinders' in entry) return sum + entry.totalCylinders;
        if ('CC' in entry && 'PC' in entry) return sum + entry.CC + entry.PC;
        return sum;
      }, 0);

      setTotalCylinders(totalFilled);
    } catch (err) {
      console.error('❌ Error fetching production stats:', err);
    }
  };

  fetchStats();
}, [month, year]);


// "Ritmo hacia la Meta" — projects month-end total from the current daily pace
// so admins know *today* whether they're on track, not just after month-end.
const pacePlan = useMemo(() => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month - 1;
  const todayDay = isCurrentMonth ? today.getDate() : daysInMonth;

  const dailyTotals = Array(daysInMonth + 1).fill(0);
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const d = new Date(entry.date);
    if (d.getFullYear() === year && d.getMonth() === month - 1) {
      const day = d.getDate();
      if (day >= 1 && day <= daysInMonth) {
        dailyTotals[day] += entry.totalCylinders || 0;
      }
    }
  });

  const chartLabels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`);
  const actual = [];
  let running = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    if (day <= todayDay) {
      running += dailyTotals[day];
      actual.push(running);
    } else {
      actual.push(null);
    }
  }

  const requiredPace = chartLabels.map((_, i) => Math.round((goal / daysInMonth) * (i + 1)));

  let projection = null;
  let projectedTotal = null;
  if (isCurrentMonth && todayDay > 0 && goal > 0) {
    const dailyAvg = actual[todayDay - 1] / todayDay;
    projectedTotal = Math.round(dailyAvg * daysInMonth);
    projection = chartLabels.map((_, i) => {
      const day = i + 1;
      if (day < todayDay) return null;
      return Math.round(dailyAvg * day);
    });
  }

  return { labels: chartLabels, actual, requiredPace, projection, projectedTotal, isCurrentMonth, todayDay, daysInMonth };
}, [entries, goal, month, year]);

const paceChartData = {
  labels: pacePlan.labels,
  datasets: [
    {
      label: 'Real',
      data: pacePlan.actual,
      borderColor: '#12B8B2',
      backgroundColor: '#12B8B2',
      tension: 0.3,
      spanGaps: true,
      pointRadius: 2,
    },
    {
      label: 'Ritmo Necesario',
      data: pacePlan.requiredPace,
      borderColor: 'rgba(234,242,245,0.35)',
      borderDash: [6, 6],
      pointRadius: 0,
      tension: 0,
    },
    ...(pacePlan.projection
      ? [
          {
            label: 'Proyección',
            data: pacePlan.projection,
            borderColor: '#F5B700',
            borderDash: [3, 3],
            pointRadius: 0,
            tension: 0.3,
            spanGaps: true,
          },
        ]
      : []),
  ],
};

const paceStatus = useMemo(() => {
  if (!goal || goal <= 0) return null;
  const projected = pacePlan.isCurrentMonth ? pacePlan.projectedTotal : pacePlan.actual[pacePlan.actual.length - 1];
  if (projected == null) return null;
  const pct = (projected / goal) * 100;
  const color = pct >= 100 ? 'var(--mm-green)' : pct >= 70 ? 'var(--mm-yellow)' : 'var(--mm-red)';
  return { projected, pct, color };
}, [goal, pacePlan]);


// --- Production/output analytics (Horas de Producción's month/year selector) ---

// 1) Tanques Llenados por Día — daily output, straight from filling records.
const dailyOutput = useMemo(() => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const totals = Array(daysInMonth + 1).fill(0);
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const d = new Date(entry.date);
    if (d.getFullYear() === year && d.getMonth() === month - 1) {
      const day = d.getDate();
      if (day >= 1 && day <= daysInMonth) totals[day] += entry.totalCylinders || 0;
    }
  });
  return {
    labels: Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`),
    data: totals.slice(1),
  };
}, [entries, month, year]);

// Tanques Recibidos por Día — empties that arrived (contributions of type
// "Recibido", not averiados/ajustes), shown next to the filled ones.
const dailyReceived = useMemo(() => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const totals = Array(daysInMonth + 1).fill(0);
  (Array.isArray(movements) ? movements : []).forEach((entry) => {
    if (entry.type !== 'Recibido') return;
    const d = new Date(entry.date);
    if (d.getFullYear() === year && d.getMonth() === month - 1) {
      const day = d.getDate();
      if (day >= 1 && day <= daysInMonth) totals[day] += entry.quantity || 0;
    }
  });
  return totals.slice(1);
}, [movements, month, year]);

// 2) Rendimiento — cylinders filled per production hour, using the (now-fixed)
// hours chart data matched against daily output.
const throughputTrend = useMemo(() => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const hoursByDay = {};
  (hoursData.calculatedHours || []).forEach((h) => { hoursByDay[h.date] = h.hours; });

  const labels = [];
  const data = [];
  let totalCyls = 0;
  let totalHrs = 0;

  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const hrs = hoursByDay[key] || 0;
    const cyls = dailyOutput.data[d - 1] || 0;
    totalCyls += cyls;
    totalHrs += hrs;
    labels.push(`${d}`);
    data.push(hrs > 0 ? Math.round((cyls / hrs) * 10) / 10 : null);
  }

  return {
    labels,
    data,
    avg: totalHrs > 0 ? Math.round((totalCyls / totalHrs) * 10) / 10 : 0,
  };
}, [hoursData, dailyOutput, month, year]);

// 3) Tendencia de Tiempo de Llenado — average minutes per filling, trended.
const fillTimeTrend = useMemo(() => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const sums = Array(daysInMonth + 1).fill(0);
  const counts = Array(daysInMonth + 1).fill(0);

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const d = new Date(entry.date);
    if (d.getFullYear() === year && d.getMonth() === month - 1 && typeof entry.totalTime === 'number') {
      const day = d.getDate();
      if (day >= 1 && day <= daysInMonth) {
        sums[day] += entry.totalTime;
        counts[day] += 1;
      }
    }
  });

  const labels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`);
  const data = labels.map((_, i) => (counts[i + 1] ? Math.round(sums[i + 1] / counts[i + 1]) : null));
  const totalCount = counts.reduce((a, b) => a + b, 0);
  const totalSum = sums.reduce((a, b) => a + b, 0);

  return { labels, data, avg: totalCount ? Math.round(totalSum / totalCount) : 0 };
}, [entries, month, year]);

// Backing data for the fillings modal — same month/year filter as the three
// charts above, just the raw records instead of a per-day aggregate.
const monthFillings = useMemo(() => {
  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => {
      const d = new Date(entry.date);
      return d.getFullYear() === year && d.getMonth() === month - 1;
    })
    .sort((a, b) => new Date(b.startTime || b.date) - new Date(a.startTime || a.date));
}, [entries, month, year]);

// Movimientos de Cilindros — same global month/year selector, so "review
// last month" actually shows last month's movements instead of paging
// through all-time history to find them.
const monthMovements = useMemo(() => {
  return (Array.isArray(movements) ? movements : []).filter((entry) => {
    const d = new Date(entry.date);
    return d.getFullYear() === year && d.getMonth() === month - 1;
  });
}, [movements, month, year]);

// Clicking directly on a chart data point narrows the modal down to just
// that day (see the three charts' onClick below) instead of the whole
// month — the point of a per-day chart is picking out one day's outlier,
// not re-scanning the whole month's list to find it again.
const modalFillings = useMemo(() => {
  if (fillingsModalDay == null) return monthFillings;
  return monthFillings.filter((entry) => new Date(entry.date).getDate() === fillingsModalDay);
}, [monthFillings, fillingsModalDay]);

const fillingsTotalPages = Math.max(1, Math.ceil(modalFillings.length / FILLINGS_PER_PAGE));
const paginatedFillings = modalFillings.slice(
  (fillingsPage - 1) * FILLINGS_PER_PAGE,
  fillingsPage * FILLINGS_PER_PAGE
);

// `day` is the clicked chart label (a day-of-month number) when the click
// landed on an actual data point, or null for "whole month" (clicking
// blank chart space, or the caption text below it).
const openFillingsModal = (day = null) => {
  setFillingsModalDay(day);
  setFillingsPage(1);
  setShowFillingsModal(true);
};

// datetime-local inputs need local (not UTC) wall-clock values — same
// conversion cylinderAdder.js's own filling-time fields already use.
const toLocalInputValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const openFillingEditModal = (filling) => {
  setEditingFilling(filling);
  setFillingEditForm({
    clients: filling.clients || '',
    ccCount: filling.ccCount ?? 0,
    pcCount: filling.pcCount ?? 0,
    startTime: filling.startTime ? toLocalInputValue(new Date(filling.startTime)) : '',
    endTime: filling.endTime ? toLocalInputValue(new Date(filling.endTime)) : '',
  });
  setFillingEditError('');
  setShowFillingEditModal(true);
};

const saveFillingEdit = async () => {
  if (!editingFilling) return;

  setFillingEditSaving(true);
  setFillingEditError('');

  try {
    const res = await fetch(`/api/fillings/${editingFilling.fillingID}/edit`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clients: fillingEditForm.clients,
        ccCount: Number(fillingEditForm.ccCount) || 0,
        pcCount: Number(fillingEditForm.pcCount) || 0,
        startTime: fillingEditForm.startTime ? new Date(fillingEditForm.startTime).toISOString() : undefined,
        endTime: fillingEditForm.endTime ? new Date(fillingEditForm.endTime).toISOString() : undefined,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al editar el llenado.');

    await fetchDashboardData();

    setShowFillingEditModal(false);
    setEditingFilling(null);
  } catch (err) {
    setFillingEditError(err.message);
  } finally {
    setFillingEditSaving(false);
  }
};

const requestFillingDelete = (filling) => {
  setFillingDeleteTarget(filling);
  setFillingDeleteError('');
  setShowFillingDeleteModal(true);
};

const confirmFillingDelete = async () => {
  if (!fillingDeleteTarget) return;

  setFillingDeleteSaving(true);
  setFillingDeleteError('');

  try {
    const res = await fetch(`/api/fillings/${fillingDeleteTarget.fillingID}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al eliminar el llenado.');

    await fetchDashboardData();

    setShowFillingDeleteModal(false);
    setFillingDeleteTarget(null);
  } catch (err) {
    setFillingDeleteError(err.message);
  } finally {
    setFillingDeleteSaving(false);
  }
};


const handleConfirmedUpdate = async () => {
  setSavingReceivedUpdate(true);
  try {
    // Re-fetch the current total right before applying — cylindersToday may
    // be stale (fetched once on mount, not polled), and computing the delta
    // against a stale baseline would silently land on the wrong absolute
    // total if anything else changed "En Planta" in the meantime.
    const freshRes = await fetch('/api/cylinders/summary');
    const freshData = await freshRes.json();
    const freshReceived = typeof freshData.received === 'number' ? freshData.received : cylindersToday;

    const delta = adjustedReceived - freshReceived;

    if (delta === 0) {
      setFeedbackMessage('⚠️ No se realizó ningún ajuste porque el valor no cambió.');
      setIsError(false);
      setShowConfirmModal(false);
      setShowFeedbackModal(true);
      return;
    }

    const res = await fetch('/api/cylinders/admin-adjustment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        delta,
        userName,
        reason: adjustReason.trim(),
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update');

    await fetchCylinderCounts();

    addLogEntry(`Admin ajustó los cilindros recibidos a ${adjustedReceived} (${delta > 0 ? '+' : ''}${delta}).`);
    setFeedbackMessage('✅ Ajuste guardado correctamente.');
    setIsError(false);
  } catch (err) {
    console.error('❌ Error updating cylinder counts:', err);
    setFeedbackMessage('❌ No se pudo actualizar los cilindros.');
    setIsError(true);
  } finally {
    setSavingReceivedUpdate(false);
    setShowConfirmModal(false);
    setShowFeedbackModal(true);
  }
};


  const handleUpdateCylinders = () => {
    if (!Number.isFinite(adjustedReceived)) {
      setFeedbackMessage('❌ Ingrese un número válido.');
      setIsError(true);
      setShowFeedbackModal(true);
      return;
    }
    if (adjustedReceived < 0) {
      setFeedbackMessage('❌ "Cilindros En Planta" no puede ser negativo.');
      setIsError(true);
      setShowFeedbackModal(true);
      return;
    }
    setAdjustReason('');
    setShowConfirmModal(true);
  };

  const handleUpdateFilled = () => {
    if (!Number.isFinite(adjustedRemaining)) {
      setFeedbackMessage('❌ Ingrese un número válido.');
      setIsError(true);
      setShowFeedbackModal(true);
      return;
    }
    if (adjustedRemaining < 0) {
      setFeedbackMessage('❌ "Por Llenar" no puede ser negativo.');
      setIsError(true);
      setShowFeedbackModal(true);
      return;
    }
    if (adjustedRemaining === remaining) {
      setFeedbackMessage('⚠️ No se realizó ningún ajuste porque el valor no cambió.');
      setIsError(false);
      setShowFeedbackModal(true);
      return;
    }
    setAdjustReason('');
    setShowFilledConfirmModal(true);
  };

  const handleConfirmedFilledUpdate = async () => {
    setSavingFilledUpdate(true);
    try {
      // Re-fetch right before applying, same reasoning as the received-side
      // adjustment: `remaining` here could be a few seconds stale, and the
      // delta needs to be computed against the true current value.
      const freshRes = await fetch('/api/cylinders/summary');
      const freshData = await freshRes.json();
      const freshRemaining = typeof freshData.remaining === 'number' ? freshData.remaining : remaining;

      // "Por Llenar" and "filled" move inversely — closing the gap by X means
      // recording X more cylinders as filled.
      const delta = freshRemaining - adjustedRemaining;

      if (delta === 0) {
        setFeedbackMessage('⚠️ No se realizó ningún ajuste porque el valor no cambió.');
        setIsError(false);
        setShowFilledConfirmModal(false);
        setShowFeedbackModal(true);
        return;
      }

      const res = await fetch('/api/fillings/admin-adjustment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delta, reason: adjustReason.trim() }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');

      refreshSummary();
      addLogEntry(`Admin ajustó "Por Llenar" a ${adjustedRemaining} (llenado ${delta > 0 ? '+' : ''}${delta}).`);
      setFeedbackMessage('✅ Ajuste guardado correctamente.');
      setIsError(false);
    } catch (err) {
      console.error('❌ Error updating filled adjustment:', err);
      setFeedbackMessage('❌ No se pudo actualizar "Por Llenar".');
      setIsError(true);
    } finally {
      setSavingFilledUpdate(false);
      setShowFilledConfirmModal(false);
      setShowFeedbackModal(true);
    }
  };

  const handleAddCylinders = () => {
    if (!Number.isFinite(additionalCylinders) || additionalCylinders <= 0) {
      alert('Ingrese una cantidad válida mayor a 0.');
      return;
    }
    if (!contributionClient) {
      alert('Por favor seleccione un cliente.');
      return;
    }
    if (!contributionGas) {
      alert('Indica el gas de estos cilindros.');
      return;
    }
    if (isCgcClient(contributionClient) && !contributionBroughtBy) {
      alert('Indica qué cliente trajo estos cilindros de CGC.');
      return;
    }
    setShowAddMovementModal(false);
    setShowAddCylindersConfirmModal(true);
  };

  const handleConfirmedAddCylinders = async () => {
    setSavingAddCylinders(true);
    try {
      const res = await fetch('/api/cylinders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: additionalCylinderType,
          quantity: additionalCylinders,
          client: contributionClient,
          gas: contributionGas,
          ...(isCgcClient(contributionClient) ? { broughtBy: contributionBroughtBy } : {}),
          date: new Date().toISOString(),
          userName,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar cilindros');
      }

      await fetchContributions();
      await fetchCylinderCounts();
      addLogEntry(`Administrador agregó ${additionalCylinders} cilindros (${additionalCylinderType}) del cliente "${contributionClient}"`);

      setAdditionalCylinders(0);
      setContributionClient('');
      setContributionGas('');
      setContributionBroughtBy('');
      setAdditionalCylinderType('CC');
    } catch (err) {
      console.error('❌ Error al agregar cilindros:', err);
      alert('No se pudieron guardar los cilindros. Intente nuevamente.');
    } finally {
      setSavingAddCylinders(false);
      setShowAddCylindersConfirmModal(false);
    }
  };

  // Fresh, full-shape contribution data for the despacho quick-add form —
  // deliberately not reusing the `cylinderContributions` state (fed by this
  // same fetchContributions()), which only keeps clientName/cylindersAdded/
  // timestamp and drops the _id/remaining/type this needs for FIFO
  // allocation. Same grouping approach as manifold-pwa's DispatchTab.
  const loadDispatchEligibleContributions = async () => {
    try {
      const res = await fetch('/api/cylinders/summary');
      const data = await res.json();
      const eligible = (data.contributions || []).filter(
        (c) => c.remaining > 0 && c.type !== 'Ajuste Administrativo'
      );
      const map = new Map();
      // one group per client and gas, so a despacho never mixes gases
      eligible.forEach((c) => {
        const key = `${c.client}|${c.gas || ''}`;
        if (!map.has(key)) map.set(key, { key, client: c.client, gas: c.gas || '', totalRemaining: 0, entries: [] });
        const group = map.get(key);
        group.totalRemaining += c.remaining;
        group.entries.push(c);
      });
      setDispatchClientGroups([...map.values()]);
    } catch (err) {
      console.error('❌ Error loading contributions for dispatch:', err);
    }
  };

  const openAddMovementModal = () => {
    setAddMovementType('contribution');
    setDispatchModalClient('');
    setDispatchModalAmount(0);
    setDispatchModalNote('');
    setDispatchModalDestination('');
    setAddDispatchError('');
    loadDispatchEligibleContributions();
    setShowAddMovementModal(true);
  };

  const handleReviewNewDispatch = () => {
    const group = dispatchClientGroups.find((g) => g.key === dispatchModalClient);

    if (!group) {
      setAddDispatchError('Seleccione un cliente con inventario disponible.');
      return;
    }
    if (!dispatchModalNote.trim()) {
      setAddDispatchError('Ingrese un número de documento.');
      return;
    }
    if (!Number.isFinite(dispatchModalAmount) || dispatchModalAmount <= 0) {
      setAddDispatchError('Ingrese una cantidad válida mayor a 0.');
      return;
    }
    if (dispatchModalAmount > group.totalRemaining) {
      setAddDispatchError(`Solo quedan ${group.totalRemaining} disponibles para "${group.client}".`);
      return;
    }
    if (isCgcClient(group.client) && !dispatchModalDestination) {
      setAddDispatchError('Los cilindros de Caribbean Gas / IntMed necesitan el cliente que los recibe: elige el Cliente Destino.');
      return;
    }

    setAddDispatchError('');
    setShowAddMovementModal(false);
    setShowAddDispatchConfirmModal(true);
  };

  const confirmNewDispatch = async () => {
    const group = dispatchClientGroups.find((g) => g.key === dispatchModalClient);
    if (!group) return;

    setAddDispatchSaving(true);
    try {
      const sortedEntries = [...group.entries].sort((a, b) => new Date(a.date) - new Date(b.date));
      const entries = [];
      let left = dispatchModalAmount;
      for (const entry of sortedEntries) {
        if (left <= 0) break;
        const take = Math.min(entry.remaining, left);
        if (take > 0) {
          entries.push({
            entryId: entry._id,
            amount: take,
            client: entry.client,
            destinationClient: dispatchModalDestination || undefined,
          });
          left -= take;
        }
      }

      const res = await fetch('/api/dispatches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ noteNumber: dispatchModalNote.trim(), entries, method: dispatchModalMethod }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al registrar el despacho.');

      await fetchMovements();
      refreshSummary();
      await fetchContributions();
      addLogEntry(`Administrador despachó ${dispatchModalAmount} cilindros de "${group.client}" (doc. ${dispatchModalNote.trim()})`);

      setDispatchModalClient('');
      setDispatchModalAmount(0);
      setDispatchModalNote('');
      setDispatchModalDestination('');
      setDispatchModalMethod('entrega');
      setShowAddDispatchConfirmModal(false);
    } catch (err) {
      console.error('❌ Error al registrar despacho:', err);
      setAddDispatchError(err.message || 'Error al registrar el despacho.');
      setShowAddDispatchConfirmModal(false);
      setShowAddMovementModal(true);
    } finally {
      setAddDispatchSaving(false);
    }
  };

  const handleAddUser = async () => {
  if (!newUser.username || !newUser.password) {
    alert('Por favor ingrese nombre de usuario y contraseña.');
    return;
  }

  try {
    const res = await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newUser, adminUsername: userName }),
    });

    if (!res.ok) throw new Error('Error al crear usuario');

    const created = await res.json();
    // API responds with { message, user: { username, type, _id } } — role
    // is keyed as "type" there but "role" everywhere this table reads it,
    // so translate rather than spread the raw response (which previously
    // left new rows showing an undefined role until the next full reload).
    setUsers([...users, { username: created.user?.username || newUser.username, role: created.user?.type || newUser.type, _id: created.user?._id }]);
    setNewUser({ username: '', password: '', type: 'filler' });
  } catch (err) {
    console.error('❌ Error creando usuario:', err);
    alert('Error al crear usuario.');
  }
};

const handleDeleteUser = async (usernameToDelete) => {
  try {
    const res = await fetch(`/api/users/${usernameToDelete}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminUsername: userName }),
    });

    if (!res.ok) throw new Error('Failed to delete user');

    setUsers(prev => prev.filter(u => u.username !== usernameToDelete));
  } catch (err) {
    console.error('❌ Error deleting user:', err);
  }
};

// Last-active/activity-count per user, joined from the same full-history
// activityLog already fetched for "Registro de Actividades" — deliberately
// NOT the month-filtered version, since "last active" should reflect true
// history regardless of which month the dashboard selector is on.
const usersActivitySummary = useMemo(() => {
  const counts = {};
  const lastSeen = {};
  (activityLog || []).forEach((log) => {
    if (!log.user) return;
    counts[log.user] = (counts[log.user] || 0) + 1;
    if (!lastSeen[log.user] || new Date(log.timestamp) > new Date(lastSeen[log.user])) {
      lastSeen[log.user] = log.timestamp;
    }
  });
  return { counts, lastSeen };
}, [activityLog]);

const handleRoleChange = async (username, newRole) => {
  setRoleSavingUsername(username);
  try {
    const res = await fetch(`/api/users/${username}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al actualizar el rol.');
    setUsers((prev) => prev.map((u) => (u.username === username ? { ...u, role: newRole } : u)));
  } catch (err) {
    console.error('❌ Error updating role:', err);
    alert(err.message || 'Error al actualizar el rol.');
  } finally {
    setRoleSavingUsername('');
  }
};

const openResetPasswordModal = (username) => {
  setResetPasswordTarget(username);
  setResetPasswordValue('');
  setResetPasswordError('');
  setShowResetPasswordModal(true);
};

const handleResetPassword = async () => {
  if (!resetPasswordValue.trim()) {
    setResetPasswordError('Ingrese una contraseña.');
    return;
  }

  setResetPasswordSaving(true);
  setResetPasswordError('');

  try {
    const res = await fetch(`/api/users/${resetPasswordTarget}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: resetPasswordValue.trim() }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al restablecer la contraseña.');

    setShowResetPasswordModal(false);
    setResetPasswordTarget('');
    setResetPasswordValue('');
  } catch (err) {
    setResetPasswordError(err.message);
  } finally {
    setResetPasswordSaving(false);
  }
};

const filteredUsers = useMemo(() => {
  const q = userSearch.trim().toLowerCase();
  if (!q) return users;
  return users.filter((u) => u.username.toLowerCase().includes(q));
}, [users, userSearch]);

const handleGoalSave = async () => {
  try {
    const res = await fetch('/api/goals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ month: month - 1, year, goal: Number(goalInput), userName }),
    });
    const data = await res.json();
    setGoal(data.goal);
    setGoalExists(true);
  } catch (err) {
    console.error('❌ Error al guardar la meta:', err);
  }
};




  const sectionLabelStyle = {
    color: 'var(--mm-muted)',
    letterSpacing: '0.06em',
    fontFamily: "'IBM Plex Mono', monospace",
  };
  const monthName = new Date(0, month - 1).toLocaleString('es-DO', { month: 'long' });

  if (initialLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ backgroundColor: 'var(--mm-bg)', minHeight: '100vh', gap: '16px' }}
      >
        <div className="spinner-border text-info" role="status" style={{ width: '3rem', height: '3rem' }} />
        <span className="text-muted">Cargando panel administrativo...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-5 px-5" style={{ backgroundColor: 'var(--mm-bg)', minHeight: '100vh' }}>

      <h2 className="text-center mb-2">Dashboard Administrativo</h2>

      {/* Single global month/year selector — drives every historical/
          analytical card below (Métricos de Llenados, Producción y Metas,
          Meta de llenados, Movimientos de Cilindros, Registro de
          Actividades). Cards showing current operational state (Cilindros
          En Planta, Estado de la Planta, Orden de
          Llenado, etc.) are intentionally unaffected — there's no such
          thing as "last month's" dryer stage. */}
      <div className="d-flex justify-content-center align-items-center mb-4" style={{ gap: '10px' }}>
        <span className="text-muted small text-uppercase" style={{ letterSpacing: '0.06em' }}>
          Viendo datos de
        </span>
        <Form.Control
          as="select"
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value))}
          style={{ width: '160px' }}
        >
          {[...Array(12)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString('es-DO', { month: 'long' })}
            </option>
          ))}
        </Form.Control>
        <Form.Control
          as="select"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          style={{ width: '110px' }}
        >
          {YEAR_OPTIONS.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </Form.Control>
        <Button
          variant="outline-info"
          size="sm"
          onClick={handleGenerateReport}
          disabled={generatingReport}
        >
          {generatingReport ? 'Generando...' : 'Generar Reporte'}
        </Button>
      </div>
      {reportError && (
        <div className="alert alert-danger text-center" style={{ maxWidth: '480px', margin: '0 auto 16px' }}>
          {reportError}
        </div>
      )}

      {/* ============================================================
          1. MÉTRICOS DE LLENADOS — top-line snapshot, highest priority
          ============================================================ */}
      <div className="text-uppercase small fw-bold mb-2" style={sectionLabelStyle}>
        Métricos de Llenados
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="card mb-3 h-100" style={{ minHeight: '190px' }}>
            <div className="card-body d-flex flex-column justify-content-center text-center">
              <h5 className="card-title mb-2">
                {month === new Date().getMonth() + 1 && year === new Date().getFullYear()
                  ? 'Llenado Este Mes'
                  : `Llenado en ${monthName}`}
              </h5>
              <p className="mb-0" style={{ color: 'var(--mm-cyan)', fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.1 }}>{thousands(filledCountMonth)}</p>
              {monthHistorical > 0 && (
                <div className="small mt-2" style={{ color: 'var(--mm-muted)' }}>
                  {filledMonthInfo.live > 0
                    ? `${thousands(monthHistorical)} del sistema anterior (hasta el ${formatDayEs(new Date(new Date(`${filledMonthInfo.liveFrom}T12:00:00`).getTime() - 86400000).toISOString().slice(0, 10))}) + ${thousands(filledMonthInfo.live)} en Manifold`
                    : 'Tanques facturados en el sistema anterior'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card mb-3 h-100" style={{ minHeight: '190px' }}>
            <div className="card-body d-flex flex-column justify-content-center text-center">
              <h5 className="card-title mb-2">Llenado Esta Semana</h5>
              <p className="mb-0" style={{ color: 'var(--mm-green)', fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.1 }}>{thousands(filledCountWeek)}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="row mb-5">
        <div className="col-md-4">
          <div className="card mb-3 h-100" style={{ minHeight: '190px' }}>
            <div className="card-body d-flex flex-column justify-content-center text-center">
              <h5 className="card-title mb-2">YTD</h5>
              <p className="mb-0" style={{ color: 'var(--mm-text)', fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.1 }}>{thousands(filledCountYTD)}</p>
              {filledYTDInfo?.historical > 0 && (
                <div className="small mt-2" style={{ color: 'var(--mm-muted)' }}>
                  {thousands(filledYTDInfo.historical)} antes del {formatDayEs(filledYTDInfo.liveFrom)} (sistema anterior) + {thousands(filledYTDInfo.live)} en Manifold
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card mb-3 h-100" style={{ minHeight: '190px' }}>
            <div className="card-body d-flex flex-column justify-content-center text-center">
              <h5 className="card-title mb-2">Total Histórico</h5>
              <p className="mb-0" style={{ color: 'var(--mm-cyan)', fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.1 }}>{filledAllInfo ? thousands(filledAllInfo.count) : '—'}</p>
              {filledAllInfo && (
                <div className="small mt-2" style={{ color: 'var(--mm-muted)' }}>
                  {filledAllInfo.since ? `Desde ${formatMonthEs(filledAllInfo.since)} · ` : ''}
                  {thousands(filledAllInfo.historical)} del sistema anterior + {thousands(filledAllInfo.live)} en Manifold
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card mb-3 h-100" style={{ minHeight: '190px' }}>
            <div className="card-body d-flex flex-column justify-content-center text-center">
              <h5 className="card-title mb-2">Tiempo Promedio de Llenados</h5>
              <p className="mb-1" style={{ color: 'var(--mm-yellow)', fontSize: '3rem', fontWeight: 700, lineHeight: 1.1 }}>{avgTime} mins</p>
              <Dropdown onSelect={(key) => setAvgRange(key)}>
                <Dropdown.Toggle size="sm" variant="light">
                  Rango: {avgRange}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="24h">24h</Dropdown.Item>
                  <Dropdown.Item eventKey="7d">7 días</Dropdown.Item>
                  <Dropdown.Item eventKey="30d">30 días</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          2. PRODUCCIÓN Y METAS — is the plant running, and on pace?
          ============================================================ */}
      <div className="text-uppercase small fw-bold mb-2" style={sectionLabelStyle}>
        Producción y Metas — {monthName} {year}
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="card shadow-sm p-3 h-100">
            <div style={{ height: 260, cursor: 'pointer' }} title="Clic en un día para ver sus llenados">
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: true, position: 'top', labels: { boxWidth: 20, font: { size: 12 } } },
                    title: { display: true, text: `Horas de Operación, O2 y N2 - ${month}/${year}`, font: { size: 16 } },
                  },
                  scales: {
                    y: { beginAtZero: true, title: { display: true, text: 'Horas', font: { size: 14 } } },
                    x: {
                      title: { display: true, text: 'Día del mes', font: { size: 14 } },
                      ticks: { color: weekendTickColor(labels, year, month - 1) },
                    },
                  },
                  // Same forgiving hit-detection as the other click-to-drill
                  // charts — index mode selects the whole day's column
                  // instead of requiring a precise pixel-on-bar click.
                  interaction: { mode: 'index', intersect: false },
                  onClick: (evt, elements) => {
                    const day = elements.length > 0 ? parseInt(chartData.labels[elements[0].index], 10) : null;
                    openFillingsModal(day);
                  },
                }}
              />
            </div>
            <div className="d-flex justify-content-center align-items-center mt-3 text-muted small">
              <span
                className="me-2"
                title="Estas cifras muestran la cantidad total de horas que la planta estuvo en operación y la cantidad de tanques llenados durante el rango de tiempo seleccionado."
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-info-circle"></i>
              </span>
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => openFillingsModal()}
                title="Clic para ver el detalle de llenados"
              >
                <strong> {Number(productionHours).toFixed(2)}</strong> h de operación • <strong>{totalCylinders}</strong> tanques llenados
              </span>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm p-4 h-100 text-center position-relative">
            {!goalExists && (
              <div className="position-absolute top-50 start-50 translate-middle px-4 py-2 rounded z-3" style={{ background: 'var(--mm-panel-2)', border: '1px solid var(--mm-line)' }}>
                <div className="text-danger fw-bold text-center fs-5">
                  No hay meta para el mes/año seleccionado
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h5 className="mb-0">Meta de llenados</h5>
                {isPastGoalMonth && <span className="text-muted small">(mes anterior — solo lectura)</span>}
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                {!isPastGoalMonth && (
                  <>
                    <input
                      type="number"
                      className="form-control px-3 py-2 shadow-sm rounded"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      placeholder="Meta"
                      style={{ width: '100px' }}
                    />
                    <button
                      className="btn btn-outline-primary px-3 py-2 shadow-sm"
                      onClick={handleGoalSave}
                      title="Guardar meta"
                    >
                      <i className="bi bi-save" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center align-items-center mb-3" style={{ height: 220 }}>
              <div className="flex-grow-1 d-flex justify-content-center align-items-center h-100" style={{ maxWidth: '50%' }}>
                <Doughnut data={goalPieData} options={{ ...goalPieOptions, maintainAspectRatio: false }} />
              </div>

              <div className="ms-4 text-center">
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: goalPercentageColor }}>
                  {goalPercentage.toFixed(1)}%
                </div>
                <div style={{ fontSize: '1.1rem' }}>de la meta cumplido</div>
              </div>
            </div>

            <div className="fw-bold">
              {filledGoalCylinders} / {goal} tanques llenados
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-5">
        <div className="col-md-3">
          <Card className="mb-4 h-100">
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Ritmo hacia la Meta</Card.Title>
              {goal > 0 ? (
                <>
                  <div style={{ height: 160 }}>
                    <Line
                      data={paceChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true },
                          x: { ticks: { maxTicksLimit: 8, color: weekendTickColor(pacePlan.labels, year, month - 1) } },
                        },
                    // Precise pixel-on-the-dot clicks (Chart.js's default
                    // intersect:true) are a bad target for "click near the
                    // spike" — index mode selects the whole day's column
                    // instead, matching wherever along that day you click.
                    interaction: { mode: 'index', intersect: false },
                      }}
                    />
                  </div>
                  {paceStatus && (
                    <div className="text-center mt-2 small text-muted">
                      {pacePlan.isCurrentMonth ? 'Proyección' : 'Total'}: <strong style={{ color: paceStatus.color }}>{paceStatus.projected} / {goal}</strong> ({paceStatus.pct.toFixed(0)}%)
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted text-center py-4 small">Defina una meta para ver el ritmo.</div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-3">
          <Card className="mb-4 h-100">
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Tanques Llenados vs Recibidos por Día</Card.Title>
              <div style={{ height: 185, cursor: 'pointer' }} title="Clic en un día para ver/editar sus llenados">
                <Bar
                  data={{
                    labels: dailyOutput.labels,
                    datasets: [
                      { label: 'Llenados', data: dailyOutput.data, backgroundColor: 'rgba(18,184,178,0.6)' },
                      { label: 'Recibidos', data: dailyReceived, backgroundColor: 'rgba(230,190,40,0.6)' },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxTicksLimit: 8, color: weekendTickColor(dailyOutput.labels, year, month - 1) } },
                    },
                    // Precise pixel-on-the-dot clicks (Chart.js's default
                    // intersect:true) are a bad target for "click near the
                    // spike" — index mode selects the whole day's column
                    // instead, matching wherever along that day you click.
                    interaction: { mode: 'index', intersect: false },
                    // Clicking a bar opens just that day; clicking blank
                    // chart space (elements empty) falls back to the whole
                    // month, same as clicking the caption below.
                    onClick: (evt, elements) => {
                      const day = elements.length > 0 ? parseInt(dailyOutput.labels[elements[0].index], 10) : null;
                      openFillingsModal(day);
                    },
                  }}
                />
              </div>
              <div
                className="text-center mt-2 small text-muted"
                style={{ cursor: 'pointer' }}
                onClick={() => openFillingsModal()}
                title="Clic para ver el detalle de llenados"
              >
                Llenados: <strong style={{ color: 'var(--mm-text)' }}>{dailyOutput.data.reduce((a, b) => a + b, 0)}</strong>
                {' · '}Recibidos: <strong style={{ color: 'var(--mm-text)' }}>{dailyReceived.reduce((a, b) => a + b, 0)}</strong>
                <span className="d-block" style={{ fontSize: '0.75rem' }}>Clic para ver detalle →</span>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-3">
          <Card className="mb-4 h-100">
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Rendimiento (Cil./Hora)</Card.Title>
              <div style={{ height: 160, cursor: 'pointer' }} title="Clic en un día para ver/editar sus llenados">
                <Line
                  data={{
                    labels: throughputTrend.labels,
                    datasets: [{
                      label: 'Cilindros/Hora',
                      data: throughputTrend.data,
                      borderColor: '#F5B700',
                      backgroundColor: '#F5B700',
                      spanGaps: true,
                      tension: 0.3,
                      pointRadius: 2,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxTicksLimit: 8, color: weekendTickColor(throughputTrend.labels, year, month - 1) } },
                    },
                    // Precise pixel-on-the-dot clicks (Chart.js's default
                    // intersect:true) are a bad target for "click near the
                    // spike" — index mode selects the whole day's column
                    // instead, matching wherever along that day you click.
                    interaction: { mode: 'index', intersect: false },
                    onClick: (evt, elements) => {
                      const day = elements.length > 0 ? parseInt(throughputTrend.labels[elements[0].index], 10) : null;
                      openFillingsModal(day);
                    },
                  }}
                />
              </div>
              <div
                className="text-center mt-2 small text-muted"
                style={{ cursor: 'pointer' }}
                onClick={() => openFillingsModal()}
                title="Clic para ver el detalle de llenados"
              >
                Promedio: <strong style={{ color: 'var(--mm-yellow)' }}>{throughputTrend.avg}</strong> cil./hora
                <span className="d-block" style={{ fontSize: '0.75rem' }}>Clic para ver detalle →</span>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-3">
          <Card className="mb-4 h-100">
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Tendencia Tiempo de Llenado</Card.Title>
              <div style={{ height: 160, cursor: 'pointer' }} title="Clic en un día para ver/editar sus llenados">
                <Line
                  data={{
                    labels: fillTimeTrend.labels,
                    datasets: [{
                      label: 'Min/Llenado',
                      data: fillTimeTrend.data,
                      borderColor: '#4ADE80',
                      backgroundColor: '#4ADE80',
                      spanGaps: true,
                      tension: 0.3,
                      pointRadius: 2,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxTicksLimit: 8, color: weekendTickColor(fillTimeTrend.labels, year, month - 1) } },
                    },
                    // Precise pixel-on-the-dot clicks (Chart.js's default
                    // intersect:true) are a bad target for "click near the
                    // spike" — index mode selects the whole day's column
                    // instead, matching wherever along that day you click.
                    interaction: { mode: 'index', intersect: false },
                    onClick: (evt, elements) => {
                      const day = elements.length > 0 ? parseInt(fillTimeTrend.labels[elements[0].index], 10) : null;
                      openFillingsModal(day);
                    },
                  }}
                />
              </div>
              <div
                className="text-center mt-2 small text-muted"
                style={{ cursor: 'pointer' }}
                onClick={() => openFillingsModal()}
                title="Clic para ver el detalle de llenados"
              >
                Promedio: <strong style={{ color: 'var(--mm-green)' }}>{fillTimeTrend.avg}</strong> min/llenado
                <span className="d-block" style={{ fontSize: '0.75rem' }}>Clic para ver detalle →</span>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* ============================================================
          3. INVENTARIO Y MOVIMIENTOS — operational cylinder tracking
          ============================================================ */}
      <div className="text-uppercase small fw-bold mb-2" style={sectionLabelStyle}>
        Inventario y Movimientos
      </div>
      <div className="row mb-5">
        <div className="col-md-6">
          <Card className="mb-4 h-100">
            <Card.Body>
              <Card.Title>Cilindros En Planta</Card.Title>
              <Form.Group className="mb-3">
                <Form.Label>Actualizar Total</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={adjustedReceived}
                  onChange={(e) => setAdjustedReceived(Number(e.target.value))}
                  />
              </Form.Group>

              <Button className="w-100 mb-3" onClick={handleUpdateCylinders}>
                Guardar Actualización
              </Button>

              <Form.Group className="mb-3">
                <Form.Label>Agregar Cilindros</Form.Label>
                <div className="d-flex" style={{ gap: '8px' }}>
                  <Form.Control
                    type="number"
                    min="1"
                    value={additionalCylinders}
                    onChange={(e) => setAdditionalCylinders(parseInt(e.target.value, 10) || 0)}
                  />
                  <Form.Select
                    style={{ maxWidth: '90px' }}
                    value={additionalCylinderType}
                    onChange={(e) => setAdditionalCylinderType(e.target.value)}
                  >
                    <option value="CC">CC</option>
                    <option value="PC">PC</option>
                  </Form.Select>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <CreatableSelect
                  isClearable
                  styles={darkSelectStyles}
                  value={contributionClient ? { label: contributionClient, value: contributionClient } : null}
                  onChange={(selected) => setContributionClient(selected ? selected.value : '')}
                  onCreateOption={async (value) => {
                    try {
                      const res = await fetch('/api/clients', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: value }),
                      });

                      const data = await res.json();

                      if (!res.ok) {
                        alert(data.error || 'Error al guardar el cliente.');
                        return;
                      }

                      await fetchClients();
                      setContributionClient(value);
                      addLogEntry(`Nuevo cliente agregado desde admin: ${value}`);
                    } catch (err) {
                      console.error('❌ Error saving client:', err);
                      alert('Error de conexión al guardar el cliente.');
                    }
                  }}
                  options={clientsList}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Gas</Form.Label>
                <Form.Select value={contributionGas} onChange={(e) => setContributionGas(e.target.value)}>
                  <option value="">Elige el gas…</option>
                  {GASES.map((g) => (
                    <option key={g} value={g}>{GAS_LABEL[g]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {isCgcClient(contributionClient) && (
                <Form.Group className="mb-3">
                  <Form.Label>Traído por (cliente que devolvió los cilindros de CGC)</Form.Label>
                  <Form.Select value={contributionBroughtBy} onChange={(e) => setContributionBroughtBy(e.target.value)}>
                    <option value="">Elige el cliente…</option>
                    <option value={CGC_CLIENT}>Ninguno — estaban en la empresa</option>
                    {clientsList.map((c) => c.value).filter((name) => !isCgcClient(name)).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <Button variant="success" className="w-100" onClick={handleAddCylinders}>
                Agregar Cilindros
              </Button>

              <hr className="my-4" />
              <h6 className="text-muted mb-3 text-center">Resumen de Cilindros</h6>
              <div className="d-flex justify-content-around">
                <Card className="text-center bg-light border-success mb-2" style={{ width: '32%' }}>
                  <Card.Body>
                    <Card.Title style={{ fontSize: '0.95rem' }}>En Planta</Card.Title>
                    <p className="fw-bold text-success" style={{ fontSize: '1.5rem', marginBottom: 0 }}>
                      {received}
                    </p>
                  </Card.Body>
                </Card>

                <Card className="text-center bg-light border-warning mb-2" style={{ width: '32%' }}>
                  <Card.Body>
                    <Card.Title style={{ fontSize: '0.95rem' }}>Por Llenar</Card.Title>
                    <p className="fw-bold text-warning" style={{ fontSize: '1.5rem', marginBottom: 0 }}>
                      {remaining}
                    </p>
                    <Form.Control
                      type="number"
                      size="sm"
                      className="mt-2"
                      value={adjustedRemaining}
                      onChange={(e) => setAdjustedRemaining(Number(e.target.value))}
                    />
                    <Button size="sm" variant="outline-warning" className="w-100 mt-1" onClick={handleUpdateFilled}>
                      Ajustar
                    </Button>
                  </Card.Body>
                </Card>

                <Card className="text-center bg-light border-danger mb-2" style={{ width: '32%' }}>
                  <Card.Body>
                    <Card.Title style={{ fontSize: '0.95rem' }}>Averiados</Card.Title>
                    <p className="fw-bold text-danger" style={{ fontSize: '1.5rem', marginBottom: 0 }}>
                      {averiados}
                    </p>
                  </Card.Body>
                </Card>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-6">
          <MovementsTable
            movements={monthMovements}
            periodLabel={`${monthName} ${year}`}
            onEditContribution={openMovementEditModal}
            onDeleteContribution={requestMovementDelete}
            onEditDispatch={openDispatchEditModal}
            onDeleteDispatch={requestDispatchDelete}
            onReturnDispatch={openDispatchReturnModal}
            onAddMovement={openAddMovementModal}
          />
        </div>
      </div>

      {/* ============================================================
          4. ADMINISTRACIÓN — back-office housekeeping, lowest urgency
          ============================================================ */}
      <div className="text-uppercase small fw-bold mb-2" style={sectionLabelStyle}>
        Administración
      </div>
      <div className="row mb-4">
        <div className="col-lg-6">
          <Card className="mb-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title className="mb-0">Gestión de Usuarios</Card.Title>
                <span className="badge bg-secondary">{users.length} usuario{users.length === 1 ? '' : 's'}</span>
              </div>

              <div className="row g-2 align-items-end mb-3">
                <div className="col-md-4">
                  <Form.Label className="small mb-1">Usuario</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nombre de usuario"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label className="small mb-1">Contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Contraseña"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label className="small mb-1">Tipo</Form.Label>
                  <Form.Select
                    value={newUser.type}
                    onChange={(e) => setNewUser({ ...newUser, type: e.target.value })}
                  >
                    <option value="filler">Llenador</option>
                    <option value="sales">Ventas</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </div>

                <div className="col-md-2">
                  <Button className="w-100" onClick={handleAddUser}>
                    <i className="bi bi-person-plus-fill me-1" /> Agregar
                  </Button>
                </div>
              </div>

              <Form.Control
                type="text"
                size="sm"
                placeholder="Buscar usuario..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="mb-2"
              />

              <div style={{ maxHeight: '260px', overflowY: 'auto' }} className="border rounded">
                <table className="table table-striped table-sm text-center mb-0 align-middle">
                  <thead className="table-light" style={{ position: 'sticky', top: '0', zIndex: 1 }}>
                    <tr>
                      <th className="text-start">Usuario</th>
                      <th>Rol</th>
                      <th>Actividad</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-muted py-3">
                          {users.length === 0 ? 'No hay usuarios registrados.' : 'Ningún usuario coincide con la búsqueda.'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user, index) => {
                        const isSelf = user.username === userName;
                        const activityCount = usersActivitySummary.counts[user.username] || 0;
                        const lastSeen = usersActivitySummary.lastSeen[user.username];
                        return (
                          <tr key={user._id || index}>
                            <td className="text-start">
                              <i className={`bi ${roleIcon(user.role)} me-2`} style={{ color: 'var(--mm-muted)' }} />
                              <strong>{user.username}</strong>
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={user.role}
                                disabled={isSelf || roleSavingUsername === user.username}
                                title={isSelf ? 'No puede cambiar su propio rol' : 'Cambiar rol'}
                                onChange={(e) => handleRoleChange(user.username, e.target.value)}
                                className={`d-inline-block w-auto ${roleBadgeClass(user.role)} text-white`}
                                style={{ minWidth: '110px' }}
                              >
                                <option value="filler">Llenador</option>
                                <option value="sales">Ventas</option>
                                <option value="admin">Admin</option>
                              </Form.Select>
                            </td>
                            <td style={{ fontSize: '0.8rem' }}>
                              <div>{activityCount} actividad{activityCount === 1 ? '' : 'es'}</div>
                              <div className="text-muted">
                                {lastSeen ? formatDateTime(lastSeen) : 'Sin actividad'}
                              </div>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0 me-2"
                                title="Restablecer contraseña"
                                onClick={() => openResetPasswordModal(user.username)}
                              >
                                <i className="bi bi-key-fill"></i>
                              </Button>
                              <Button
                                variant="link"
                                className="text-danger p-0"
                                title="Eliminar usuario"
                                onClick={() => {
                                  setUserToDelete(user.username);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <i className="bi bi-trash3"></i>
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-lg-6">
          <Card className="mb-4 h-100">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: '8px' }}>
                <Card.Title className="mb-0">Registro de Actividades — {monthName} {year}</Card.Title>
                <div className="d-flex" style={{ gap: '8px' }}>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Buscar..."
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                    style={{ width: '140px' }}
                  />
                  <Form.Select
                    size="sm"
                    value={activityUserFilter}
                    onChange={(e) => setActivityUserFilter(e.target.value)}
                    style={{ width: '140px' }}
                  >
                    <option value="">Todos los usuarios</option>
                    {activityUsers.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="table-responsive" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                <table className="table table-sm align-middle">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th style={{ width: '35%', fontSize: '0.9rem' }}>Fecha y Hora</th>
                      <th style={{ fontSize: '0.9rem', textAlign: 'center' }}>Actividad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedActivityLog.length > 0 ? (
                      paginatedActivityLog.map((log, index) => (
                        <tr key={index}>
                          <td style={{ fontSize: '0.85rem', color: 'var(--mm-muted)' }}>
                            {formatDateTime(log.timestamp)}
                          </td>
                          <td style={{ fontSize: '0.95rem', textAlign: 'center' }}>{log.message}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-muted text-center">
                          No hay actividad reciente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">
                  Página {activityPage} de {activityTotalPages} — {filteredActivityLog.length} registros
                </small>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={activityPage === 1}
                    onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={activityPage === activityTotalPages}
                    onClick={() => setActivityPage((p) => Math.min(activityTotalPages, p + 1))}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="w-100 mt-2">
        <PlantHealthPanel />
        <CompliancePanel />
        <OperationsInsightsPanel />
        <PickupsDeliveriesPanel />
        <SealsPanel />
        <CgcTanksPanel />
        <VentasSyncPanel />
        <StaffActivityPanel activityLog={activityLog} users={users} fillings={entries} />
      </div>


      {/* Modals */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Actualización</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          ¿Actualizar cilindros disponibles a {adjustedReceived}?
          <div className="form-group mt-3 mb-0 text-left">
            <label className="form-label">Razón del ajuste (obligatoria)</label>
            <textarea className="form-control" rows={2} maxLength={300} value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={savingReceivedUpdate}>Cancelar</Button>
          <Button variant="primary" onClick={handleConfirmedUpdate} disabled={savingReceivedUpdate || !adjustReason.trim()}>
            {savingReceivedUpdate ? 'Guardando…' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFilledConfirmModal} onHide={() => setShowFilledConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Ajuste</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          ¿Ajustar "Por Llenar" de {remaining} a {adjustedRemaining}?
          <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
            Esto registra un ajuste administrativo de llenado ({remaining - adjustedRemaining > 0 ? '+' : ''}{remaining - adjustedRemaining} cilindros), no un llenado real.
          </div>
          <div className="form-group mt-3 mb-0 text-left">
            <label className="form-label">Razón del ajuste (obligatoria)</label>
            <textarea className="form-control" rows={2} maxLength={300} value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilledConfirmModal(false)} disabled={savingFilledUpdate}>Cancelar</Button>
          <Button variant="primary" onClick={handleConfirmedFilledUpdate} disabled={savingFilledUpdate || !adjustReason.trim()}>
            {savingFilledUpdate ? 'Guardando…' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddCylindersConfirmModal} onHide={() => setShowAddCylindersConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Cilindros</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          ¿Agregar {additionalCylinders} cilindros ({additionalCylinderType}, {gasLabel(contributionGas)}) de "{contributionClient}"
          {isCgcClient(contributionClient) && contributionBroughtBy ? `, traídos por ${contributionBroughtBy}` : ''}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddCylindersConfirmModal(false)} disabled={savingAddCylinders}>Cancelar</Button>
          <Button variant="success" onClick={handleConfirmedAddCylinders} disabled={savingAddCylinders}>
            {savingAddCylinders ? 'Guardando…' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} centered>
        <Modal.Body className="text-center">
          <p className={`fs-5 ${isError ? 'text-danger' : 'text-success'}`}>{feedbackMessage}</p>
          <Button variant="primary" onClick={() => setShowFeedbackModal(false)}>OK</Button>
        </Modal.Body>
      </Modal>

      <Modal show={showAddMovementModal} onHide={() => setShowAddMovementModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Agregar Movimiento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addDispatchError && <div className="alert alert-danger">{addDispatchError}</div>}

          <div className="btn-group w-100 mb-3">
            <Button
              variant={addMovementType === 'contribution' ? 'success' : 'outline-success'}
              onClick={() => setAddMovementType('contribution')}
            >
              Contribución
            </Button>
            <Button
              variant={addMovementType === 'dispatch' ? 'success' : 'outline-success'}
              onClick={() => setAddMovementType('dispatch')}
            >
              Despacho
            </Button>
          </div>

          {addMovementType === 'contribution' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Cantidad</Form.Label>
                <div className="d-flex" style={{ gap: '8px' }}>
                  <Form.Control
                    type="number"
                    min="1"
                    value={additionalCylinders}
                    onChange={(e) => setAdditionalCylinders(parseInt(e.target.value, 10) || 0)}
                  />
                  <Form.Select
                    style={{ maxWidth: '90px' }}
                    value={additionalCylinderType}
                    onChange={(e) => setAdditionalCylinderType(e.target.value)}
                  >
                    <option value="CC">CC</option>
                    <option value="PC">PC</option>
                  </Form.Select>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <CreatableSelect
                  isClearable
                  styles={darkSelectStyles}
                  value={contributionClient ? { label: contributionClient, value: contributionClient } : null}
                  onChange={(selected) => setContributionClient(selected ? selected.value : '')}
                  onCreateOption={async (value) => {
                    try {
                      const res = await fetch('/api/clients', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: value }),
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        alert(data.error || 'Error al guardar el cliente.');
                        return;
                      }
                      await fetchClients();
                      setContributionClient(value);
                      addLogEntry(`Nuevo cliente agregado desde admin: ${value}`);
                    } catch (err) {
                      console.error('❌ Error saving client:', err);
                      alert('Error de conexión al guardar el cliente.');
                    }
                  }}
                  options={clientsList}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Gas</Form.Label>
                <Form.Select value={contributionGas} onChange={(e) => setContributionGas(e.target.value)}>
                  <option value="">Elige el gas…</option>
                  {GASES.map((g) => (
                    <option key={g} value={g}>{GAS_LABEL[g]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {isCgcClient(contributionClient) && (
                <Form.Group className="mb-3">
                  <Form.Label>Traído por (cliente que devolvió los cilindros de CGC)</Form.Label>
                  <Form.Select value={contributionBroughtBy} onChange={(e) => setContributionBroughtBy(e.target.value)}>
                    <option value="">Elige el cliente…</option>
                    <option value={CGC_CLIENT}>Ninguno — estaban en la empresa</option>
                    {clientsList.map((c) => c.value).filter((name) => !isCgcClient(name)).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={dispatchModalClient}
                  onChange={(e) => setDispatchModalClient(e.target.value)}
                >
                  <option value="">Seleccione un cliente...</option>
                  {dispatchClientGroups.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.client} · {gasLabel(g.gas)} — {g.totalRemaining} disponibles
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Cantidad</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={dispatchModalAmount}
                  onChange={(e) => setDispatchModalAmount(parseInt(e.target.value, 10) || 0)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Número de Documento</Form.Label>
                <Form.Control
                  type="text"
                  value={dispatchModalNote}
                  onChange={(e) => setDispatchModalNote(e.target.value)}
                  placeholder="Ej: F1157"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  {isCgcClient(dispatchModalClient.split('|')[0])
                    ? 'Cliente Destino (obligatorio: ¿quién recibe los cilindros de CGC?)'
                    : 'Cliente Destino (si es un intercambio)'}
                </Form.Label>
                <Form.Select
                  value={dispatchModalDestination}
                  onChange={(e) => setDispatchModalDestination(e.target.value)}
                >
                  <option value="">{isCgcClient(dispatchModalClient.split('|')[0]) ? 'Elige el cliente…' : 'No es un intercambio'}</option>
                  {isCgcClient(dispatchModalClient.split('|')[0]) && (
                    <option value={CGC_CLIENT}>Uso interno — se quedan en la empresa</option>
                  )}
                  {clientsList
                    .map((c) => c.value)
                    .filter((name) => name !== dispatchModalClient.split('|')[0])
                    .map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Cómo sale</Form.Label>
                <Form.Select value={dispatchModalMethod} onChange={(e) => setDispatchModalMethod(e.target.value)}>
                  <option value="entrega">Entrega (se confirma después)</option>
                  <option value="retiro">Retiro en planta (entregado ahora)</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddMovementModal(false)}>Cancelar</Button>
          {addMovementType === 'contribution' ? (
            <Button variant="success" onClick={handleAddCylinders}>Continuar</Button>
          ) : (
            <Button variant="success" onClick={handleReviewNewDispatch}>Continuar</Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showAddDispatchConfirmModal} onHide={() => setShowAddDispatchConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Despacho</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          {addDispatchError && <div className="alert alert-danger">{addDispatchError}</div>}
          ¿Despachar {dispatchModalAmount} cilindros de "{dispatchModalClient.split('|')[0]}" ({gasLabel(dispatchModalClient.split('|')[1])}) con documento "{dispatchModalNote}"{dispatchModalMethod === 'retiro' ? ', retirados en planta' : ', para entrega'}?
          {dispatchModalDestination && (
            <> Intercambio: destinado a <strong>{dispatchModalDestination}</strong>.</>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddDispatchConfirmModal(false)} disabled={addDispatchSaving}>Cancelar</Button>
          <Button variant="success" onClick={confirmNewDispatch} disabled={addDispatchSaving}>
            {addDispatchSaving ? 'Guardando…' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>Confirmar Eliminación</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    ¿Estás seguro que deseas eliminar al usuario <strong>{userToDelete}</strong>?
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
      Cancelar
    </Button>
    <Button
      variant="danger"
      onClick={async () => {
        await handleDeleteUser(userToDelete);
        setShowDeleteModal(false);
        setUserToDelete(null);
      }}
    >
      Eliminar
    </Button>
  </Modal.Footer>
</Modal>

      <Modal
        show={showResetPasswordModal}
        onHide={() => (resetPasswordSaving ? null : setShowResetPasswordModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Restablecer Contraseña</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetPasswordError && <div className="alert alert-danger">{resetPasswordError}</div>}
          <p>
            Nueva contraseña para <strong>{resetPasswordTarget}</strong>:
          </p>
          <Form.Control
            type="text"
            placeholder="Nueva contraseña"
            value={resetPasswordValue}
            onChange={(e) => setResetPasswordValue(e.target.value)}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowResetPasswordModal(false)}
            disabled={resetPasswordSaving}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleResetPassword} disabled={resetPasswordSaving}>
            {resetPasswordSaving ? 'Guardando...' : 'Restablecer'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMovementEditModal}
        onHide={() => (movementEditSaving ? null : setShowMovementEditModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Contribución</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {movementEditError && (
            <div className="alert alert-danger">{movementEditError}</div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Cliente</Form.Label>
            <Form.Control type="text" value={editingMovement?.client || ''} disabled />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tipo</Form.Label>
            <Form.Select
              value={movementEditForm.type}
              onChange={(e) => setMovementEditForm((f) => ({ ...f, type: e.target.value }))}
            >
              <option value="CC">CC</option>
              <option value="PC">PC</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cantidad</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={movementEditForm.quantity}
              onChange={(e) => setMovementEditForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={movementEditForm.date}
              onChange={(e) => setMovementEditForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMovementEditModal(false)}
            disabled={movementEditSaving}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveMovementEdit} disabled={movementEditSaving}>
            {movementEditSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMovementDeleteModal}
        onHide={() => (movementDeleteSaving ? null : setShowMovementDeleteModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {movementDeleteError && (
            <div className="alert alert-danger">{movementDeleteError}</div>
          )}
          ¿Está seguro que desea eliminar la contribución de{' '}
          <strong>{movementDeleteTarget?.quantity}</strong> cilindros (
          <strong>{movementDeleteTarget?.cylinderType}</strong>) para{' '}
          <strong>{movementDeleteTarget?.client}</strong>? Esta acción no se puede
          deshacer.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMovementDeleteModal(false)}
            disabled={movementDeleteSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmMovementDelete}
            disabled={movementDeleteSaving}
          >
            {movementDeleteSaving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchEditModal}
        onHide={() => (dispatchEditSaving ? null : setShowDispatchEditModal(false))}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Despacho</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchEditError && (
            <div className="alert alert-danger">{dispatchEditError}</div>
          )}

          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Label>Número de Documento</Form.Label>
              <Form.Control
                type="text"
                value={dispatchEditForm.noteNumber}
                onChange={(e) =>
                  setDispatchEditForm((f) => ({ ...f, noteNumber: e.target.value }))
                }
              />
            </div>
            <div className="col-md-6 mb-3">
              <Form.Label>Despachado Por</Form.Label>
              <Form.Control
                type="text"
                value={dispatchEditForm.dispatchedBy}
                onChange={(e) =>
                  setDispatchEditForm((f) => ({ ...f, dispatchedBy: e.target.value }))
                }
              />
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Cliente Destino (si es un intercambio)</Form.Label>
            <Form.Select
              value={dispatchEditForm.destinationClient}
              onChange={(e) =>
                setDispatchEditForm((f) => ({ ...f, destinationClient: e.target.value }))
              }
            >
              <option value="">No es un intercambio</option>
              {clientsList
                .map((c) => c.value)
                .filter((name) => !dispatchEditForm.contributions.some((c) => c.client === name))
                .map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
            </Form.Select>
          </Form.Group>

          <Form.Label className="mt-2">Cilindros por Cliente</Form.Label>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {dispatchEditForm.contributions.map((c, index) => (
                <tr key={`${c.entryId}-${index}`}>
                  <td>{c.client}</td>
                  <td>
                    <Form.Control
                      type="number"
                      min="1"
                      value={c.amount}
                      onChange={(e) => updateDispatchEditAmount(index, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchEditModal(false)}
            disabled={dispatchEditSaving}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveDispatchEdit} disabled={dispatchEditSaving}>
            {dispatchEditSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchDeleteModal}
        onHide={() => (dispatchDeleteSaving ? null : setShowDispatchDeleteModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchDeleteError && (
            <div className="alert alert-danger">{dispatchDeleteError}</div>
          )}
          ¿Está seguro que desea eliminar el despacho con documento{' '}
          <strong>{dispatchDeleteTarget?.noteNumber}</strong> por{' '}
          <strong>
            {(dispatchDeleteTarget?.contributions || []).reduce((s, c) => s + (c.amount || 0), 0)}
          </strong>{' '}
          cilindros? Los cilindros despachados volverán a estar disponibles. Esta acción no se
          puede deshacer.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchDeleteModal(false)}
            disabled={dispatchDeleteSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDispatchDelete}
            disabled={dispatchDeleteSaving}
          >
            {dispatchDeleteSaving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchReturnModal}
        onHide={() => (dispatchReturnSaving ? null : setShowDispatchReturnModal(false))}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Registrar Devolución — {returningDispatch?.noteNumber}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchReturnError && (
            <div className="alert alert-danger">{dispatchReturnError}</div>
          )}

          <p className="text-muted small">
            Indique cuántos cilindros de cada línea no fueron aceptados por el
            cliente. Se sumarán de vuelta a "En Planta" y a lo pendiente de
            despachar en la contribución de origen — no afectan "Por Llenar",
            ya que estos tanques ya estaban llenos.
          </p>

          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Despachado</th>
                <th>Ya Devuelto</th>
                <th>Cantidad a Devolver</th>
              </tr>
            </thead>
            <tbody>
              {dispatchReturnLines.map((line, index) => (
                <tr key={`${line.entryId}-${index}`}>
                  <td>{line.client}</td>
                  <td>{line.originalAmount}</td>
                  <td>{line.alreadyReturned}</td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      max={line.remaining}
                      placeholder="0"
                      disabled={line.remaining <= 0}
                      value={line.returnQty}
                      onChange={(e) => updateDispatchReturnQty(index, e.target.value)}
                    />
                    {line.remaining <= 0 && (
                      <small className="text-muted">Nada pendiente</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Form.Group className="mb-2">
            <Form.Label>Motivo (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Ej: Cliente rechazó 10 tanques por daño en el sello."
              value={dispatchReturnReason}
              onChange={(e) => setDispatchReturnReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchReturnModal(false)}
            disabled={dispatchReturnSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="warning"
            onClick={saveDispatchReturn}
            disabled={dispatchReturnSaving}
          >
            {dispatchReturnSaving ? 'Registrando...' : 'Registrar Devolución'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFillingsModal} onHide={() => setShowFillingsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Llenados —{' '}
            {fillingsModalDay != null
              ? `${fillingsModalDay}/${month}/${year}`
              : `${monthName} ${year}`}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {fillingsModalDay != null && (
            <div className="mb-3">
              <Button variant="link" className="p-0" onClick={() => { setFillingsModalDay(null); setFillingsPage(1); }}>
                ← Ver todo el mes
              </Button>
            </div>
          )}
          {modalFillings.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cliente(s)</th>
                      <th>CC</th>
                      <th>PC</th>
                      <th>Total</th>
                      <th>Tiempo (min)</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFillings.map((filling) => (
                      <tr key={filling.fillingID || filling._id}>
                        <td style={{ fontSize: '0.85rem' }}>
                          {formatDateTime(filling.startTime || filling.date)}
                        </td>
                        <td>{filling.clients || '—'}</td>
                        <td>{filling.ccCount ?? 0}</td>
                        <td>{filling.pcCount ?? 0}</td>
                        <td>{filling.totalCylinders ?? ((filling.ccCount || 0) + (filling.pcCount || 0))}</td>
                        <td>{typeof filling.totalTime === 'number' ? filling.totalTime : '—'}</td>
                        <td>
                          <div className="d-flex" style={{ gap: '4px' }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              title="Editar llenado"
                              onClick={() => openFillingEditModal(filling)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Eliminar llenado"
                              onClick={() => requestFillingDelete(filling)}
                            >
                              <i className="bi bi-trash3"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={fillingsPage === 1}
                  onClick={() => setFillingsPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Página {fillingsPage} de {fillingsTotalPages} — {modalFillings.length} llenados
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={fillingsPage === fillingsTotalPages}
                  onClick={() => setFillingsPage((p) => Math.min(fillingsTotalPages, p + 1))}
                >
                  Siguiente
                </Button>
              </div>
            </>
          ) : (
            <div className="text-muted text-center py-4">
              {fillingsModalDay != null ? 'Sin llenados registrados para este día.' : 'Sin llenados registrados para este mes.'}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFillingsModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showFillingEditModal}
        onHide={() => (fillingEditSaving ? null : setShowFillingEditModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Llenado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {fillingEditError && <div className="alert alert-danger">{fillingEditError}</div>}

          <Form.Group className="mb-3">
            <Form.Label>Cliente(s)</Form.Label>
            <Form.Control
              type="text"
              value={fillingEditForm.clients}
              onChange={(e) => setFillingEditForm((f) => ({ ...f, clients: e.target.value }))}
            />
          </Form.Group>

          <div className="row">
            <div className="col-6 mb-3">
              <Form.Label>CC</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={fillingEditForm.ccCount}
                onChange={(e) => setFillingEditForm((f) => ({ ...f, ccCount: e.target.value }))}
              />
            </div>
            <div className="col-6 mb-3">
              <Form.Label>PC</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={fillingEditForm.pcCount}
                onChange={(e) => setFillingEditForm((f) => ({ ...f, pcCount: e.target.value }))}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6 mb-3">
              <Form.Label>Hora de Inicio</Form.Label>
              <Form.Control
                type="datetime-local"
                value={fillingEditForm.startTime}
                onChange={(e) => setFillingEditForm((f) => ({ ...f, startTime: e.target.value }))}
              />
            </div>
            <div className="col-6 mb-3">
              <Form.Label>Hora de Fin</Form.Label>
              <Form.Control
                type="datetime-local"
                value={fillingEditForm.endTime}
                onChange={(e) => setFillingEditForm((f) => ({ ...f, endTime: e.target.value }))}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowFillingEditModal(false)}
            disabled={fillingEditSaving}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveFillingEdit} disabled={fillingEditSaving}>
            {fillingEditSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showFillingDeleteModal}
        onHide={() => (fillingDeleteSaving ? null : setShowFillingDeleteModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {fillingDeleteError && <div className="alert alert-danger">{fillingDeleteError}</div>}
          ¿Está seguro que desea eliminar el llenado{' '}
          <strong>{fillingDeleteTarget?.fillingID}</strong> de{' '}
          <strong>{fillingDeleteTarget?.clients || '—'}</strong> (
          {fillingDeleteTarget?.totalCylinders ?? ((fillingDeleteTarget?.ccCount || 0) + (fillingDeleteTarget?.pcCount || 0))}{' '}
          cilindros)? Esta acción no se puede deshacer.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowFillingDeleteModal(false)}
            disabled={fillingDeleteSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmFillingDelete}
            disabled={fillingDeleteSaving}
          >
            {fillingDeleteSaving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default AdminDashboard;
